/**
 * 플랫폼 API(api2) — 클래스 랩 / 실습기록
 * @see docs/api/studyResultOld.json, REACT_APP_STUDY_RESULT_API_BASE_URL
 *
 * 마지막 인자 `platformAccessToken`이 `undefined`가 아니면 해당 문자열만 Bearer로 전송(웹 JWT와 분리).
 */

import { platformGet } from './platformApiClient';

/** 나의 실습기록 / 관리자 stat 공통 쿼리 (studyResultOld §1.1, §2.1) */
export interface LabPlayRecordListQueryParams {
	labType?: string;
	studyDateFrom?: string;
	studyDateTo?: string;
	status?: string;
	keyword?: string;
	countPerPage?: number;
	pageNum?: number;
	sortBy?: string;
	sortOrder?: string;
}

function compactLabQueryParams(params?: LabPlayRecordListQueryParams): Record<string, string | number | boolean | undefined> {
	if (!params) return {};
	const out: Record<string, string | number | boolean | undefined> = {};
	const assign = (k: keyof LabPlayRecordListQueryParams) => {
		const v = params[k];
		if (v !== undefined && v !== null && v !== '') out[k] = v as string | number;
	};
	(
		[
			'labType',
			'studyDateFrom',
			'studyDateTo',
			'status',
			'keyword',
			'sortBy',
			'sortOrder',
		] as const
	).forEach(assign);
	if (params.countPerPage != null) out.countPerPage = params.countPerPage;
	if (params.pageNum != null) out.pageNum = params.pageNum;
	return out;
}

function authOpt(platformAccessToken: string | undefined) {
	return platformAccessToken !== undefined ? { accessToken: platformAccessToken } : {};
}

/**
 * 나의 실습기록 리스트 — GET class/lab/list/play-record/user/{user_id}
 */
export async function getMyLabPlayRecordList(
	userId: string,
	params?: LabPlayRecordListQueryParams,
	platformAccessToken?: string
): Promise<unknown> {
	const uid = encodeURIComponent(userId);
	return platformGet<unknown>(`class/lab/list/play-record/user/${uid}`, {
		params: {
			countPerPage: params?.countPerPage ?? 10,
			pageNum: params?.pageNum ?? 1,
			...compactLabQueryParams(params),
		},
		...authOpt(platformAccessToken),
	});
}

/**
 * 실습기록 리스트 (관리자·통계) — GET class/lab/list/play-record/stat/{user_id}
 * (배포 스테이지에 경로가 없으면 404일 수 있음)
 */
export async function getAdminLabPlayRecordStatList(
	userId: string,
	params?: LabPlayRecordListQueryParams,
	platformAccessToken?: string
): Promise<unknown> {
	const uid = encodeURIComponent(userId);
	return platformGet<unknown>(`class/lab/list/play-record/stat/${uid}`, {
		params: {
			countPerPage: params?.countPerPage ?? 10,
			pageNum: params?.pageNum ?? 1,
			...compactLabQueryParams(params),
		},
		...authOpt(platformAccessToken),
	});
}

export interface AdminClassUserPlayRecordsParams {
	countPerPage?: number;
	pageNum?: number;
	sortBy?: string;
	/** API 예시: sortOrder: false (boolean) */
	sortOrder?: boolean | string;
	contentCode?: string;
	mode?: string;
	keyword?: string;
}

/**
 * 실습기록(관리자) — 클래스 단위 플레이 기록 리스트
 * GET class/lab/users/play-records/{class_id}
 */
export async function getAdminClassUserPlayRecords(
	classId: string | number,
	params?: AdminClassUserPlayRecordsParams,
	platformAccessToken?: string
): Promise<unknown> {
	const cid = String(classId);
	return platformGet<unknown>(`class/lab/users/play-records/${encodeURIComponent(cid)}`, {
		classId: cid,
		params: {
			countPerPage: params?.countPerPage ?? 50,
			pageNum: params?.pageNum ?? 1,
			...(params?.sortBy != null && params.sortBy !== '' && { sortBy: params.sortBy }),
			...(params?.sortOrder !== undefined && { sortOrder: String(params.sortOrder) }),
			...(params?.contentCode != null && params.contentCode !== '' && { contentCode: params.contentCode }),
			...(params?.mode != null && params.mode !== '' && { mode: params.mode }),
			...(params?.keyword != null && params.keyword !== '' && { keyword: params.keyword }),
		},
		...authOpt(platformAccessToken),
	});
}

/**
 * 나의 실습기록 상세 — GET class/lab/user/play-records/{class_user_id} (studyResultOld §1.2)
 */
export async function getMyUserPlayRecordsDetail(
	classUserId: string,
	params?: LabPlayRecordListQueryParams,
	platformAccessToken?: string
): Promise<unknown> {
	const id = encodeURIComponent(classUserId);
	return platformGet<unknown>(`class/lab/user/play-records/${id}`, {
		params: {
			countPerPage: params?.countPerPage ?? 20,
			pageNum: params?.pageNum ?? 1,
			...compactLabQueryParams(params),
		},
		...authOpt(platformAccessToken),
	});
}

/**
 * 평가내역 상세 (관리자) — GET class/lab/users/play-record/stat/{class_id} (studyResultOld §2.3)
 */
export async function getAdminClassPlayRecordStat(
	classId: string | number,
	params?: AdminClassUserPlayRecordsParams,
	platformAccessToken?: string
): Promise<unknown> {
	const cid = String(classId);
	return platformGet<unknown>(`class/lab/users/play-record/stat/${encodeURIComponent(cid)}`, {
		classId: cid,
		params: {
			countPerPage: params?.countPerPage ?? 50,
			pageNum: params?.pageNum ?? 1,
			...(params?.sortBy != null && params.sortBy !== '' && { sortBy: params.sortBy }),
			...(params?.sortOrder !== undefined && { sortOrder: String(params.sortOrder) }),
			...(params?.contentCode != null && params.contentCode !== '' && { contentCode: params.contentCode }),
			...(params?.mode != null && params.mode !== '' && { mode: params.mode }),
			...(params?.keyword != null && params.keyword !== '' && { keyword: params.keyword }),
		},
		...authOpt(platformAccessToken),
	});
}

/**
 * 마이랩 리스트 — GET class/lab/list/user/{userId} (studyResultOld §9.1)
 */
export async function getMyLabListForUser(userId: string, platformAccessToken?: string): Promise<unknown> {
	const uid = encodeURIComponent(userId);
	return platformGet<unknown>(`class/lab/list/user/${uid}`, {
		...authOpt(platformAccessToken),
	});
}

/**
 * 편집시나리오 리스트 — GET class/custom-scenario/list/{class_id}/{content_id} (studyResultOld §10.1)
 */
export async function getCustomScenarioList(
	classId: string,
	contentId: string,
	platformAccessToken?: string
): Promise<unknown> {
	return platformGet<unknown>(
		`class/custom-scenario/list/${encodeURIComponent(classId)}/${encodeURIComponent(contentId)}`,
		{ ...authOpt(platformAccessToken) }
	);
}

/**
 * 콘텐츠 리스트 — GET {lang}/content/list (studyResultOld §10.2)
 */
export async function getPlatformContentList(lang: string, platformAccessToken?: string): Promise<unknown> {
	const l = lang.replace(/^\/+/, '').replace(/\/+$/, '') || 'ko';
	return platformGet<unknown>(`${encodeURIComponent(l)}/content/list`, { ...authOpt(platformAccessToken) });
}

/**
 * 신경계사정 등 NA 평가결과 — GET scenario/result/na/{type} (studyResultOld §4.2)
 */
export async function getScenarioResultNa(type: string, platformAccessToken?: string): Promise<unknown> {
	return platformGet<unknown>(`scenario/result/na/${encodeURIComponent(type)}`, { ...authOpt(platformAccessToken) });
}

/**
 * 정맥주사 등 SI 평가결과 — GET {lang}/scenario/result/si/{type} (studyResultOld §4.3)
 */
export async function getScenarioResultSi(
	lang: string,
	type: string,
	platformAccessToken?: string
): Promise<unknown> {
	const l = lang.replace(/^\/+/, '').replace(/\/+$/, '') || 'ko';
	return platformGet<unknown>(`${encodeURIComponent(l)}/scenario/result/si/${encodeURIComponent(type)}`, {
		...authOpt(platformAccessToken),
	});
}

/**
 * ACLS 플레이 기록 리스트 (관리자) — GET {lang}/scenario-acls/result/list/{userId} (studyResultOld §6.4)
 * `{lang}` 은 `ko`·`en` 등 로케일 코드 (문서 예시, `kor`·숫자 `1` 아님).
 */
export async function getScenarioAclsResultListForUser(
	lang: string,
	userId: string,
	platformAccessToken?: string
): Promise<unknown> {
	const l = lang.replace(/^\/+/, '').replace(/\/+$/, '') || 'ko';
	const uid = encodeURIComponent(userId);
	return platformGet<unknown>(`${encodeURIComponent(l)}/scenario-acls/result/list/${uid}`, {
		...authOpt(platformAccessToken),
	});
}

/**
 * ACLS 전체 참가자 통계 (관리자) — GET {lang}/scenario-acls/results/lab/stat/{mode}
 * 전체 참가자의 술기별 평균 정확도, 클래스 통계, 학생 순위 등을 반환
 * @param lang 언어 코드 (ko, en 등)
 * @param mode 모드 값 (시나리오의 modeValue)
 * @param classId 클래스 ID (x-class-id 헤더로 전달됨)
 * @param platformAccessToken 플랫폼 액세스 토큰
 */
export async function getAclsAllParticipantsStat(
	lang: string,
	mode: string,
	classId: number,
	platformAccessToken?: string
): Promise<unknown> {
	const l = lang.replace(/^\/+/, '').replace(/\/+$/, '') || 'ko';
	const m = encodeURIComponent(mode);
	return platformGet<unknown>(`${encodeURIComponent(l)}/scenario-acls/results/lab/stat/${m}`, {
		classId, // x-class-id 헤더로 자동 변환됨
		...authOpt(platformAccessToken),
	});
}
